import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { CompositeAsset } from "../../../../common/api/decimal/common-types/manageAssets.ts";
import useStoreLinks from "../../../../common/features/store/useStoreLinks.ts";
import useFavorites from "../../actions/favorites/useFavorites.tsx";
import { getReaderNavLink } from "../../../bookshelfLinks.ts";
import {
  canBeOpenedInReader,
  canLaunchCourseware,
  canShowCoursewareDetails,
  isBook,
  isCourseware,
  isExpiringSoon,
} from "../../../../common/features/asset/assetHelpers.ts";
import {
  ExploreAssetAction,
  useExploreAssetActionType,
} from "../explore/useExploreAssetActionType.ts";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import useAccessibilityClaimsLink from "../accessibility-claims/useAccessibilityClaimsLink.tsx";

export type PopoverLinkDescriptor =
  | "open-book"
  | "asset-details"
  | "view-ancillaries"
  | "access-code-instructions"
  | "access-code-website"
  | "borrow"
  | "return"
  | "buy-on-store"
  | "search-content"
  | "accessibility-claims";

export interface PopoverLink {
  label: string;
  onClick?: () => void;
  href?: string;
  to?: string;
  descriptor: PopoverLinkDescriptor;
}

export interface PopoverActions {
  openCoursewareDialog: () => void;
  openDetailsPanel: () => void;
  openAncillariesPanel: () => void;
  returnAsset?: () => void;
  activateAsset?: () => void;
  openContentSearchPanel?: () => void;
  openAccessibilityPanel?: () => void;
}

export default function usePopoverLinks(
  asset: CompositeAsset,
  actions: PopoverActions,
  opts: { showContentSearch?: boolean } = {}
) {
  const { isStoreAsset, makeRenewalUrl } = useStoreLinks();
  const { favoritesMenuLink } = useFavorites(asset.manageAsset, asset.favorite);
  const exploreAssetAction = useExploreAssetActionType(asset.explore);
  // will get manage reader url if not explore, won't be used if explore asset needs to borrow
  // as the borrow link will be there instead, this will call pre-activate route on open if needed
  const readerURL = getReaderNavLink(asset.manageAsset.vbid, {
    needsActivation: asset.explore && !asset.explore.active,
  });
  const intl = useIntl();
  const brand = useBrandLoaderData();
  const isReturnable = !!(asset.explore?.returnable && asset.explore?.active);
  const a11yClaimsMenuLink = useAccessibilityClaimsLink(
    actions.openAccessibilityPanel
  );

  const getExpiredLinks = useCallback(() => {
    const links: PopoverLink[] = [];
    if (asset.license && isStoreAsset(asset.license)) {
      links.push({
        descriptor: "buy-on-store",
        label: intl.formatMessage({ defaultMessage: "Get Access" }),
        href: makeRenewalUrl(asset.manageAsset.vbid, {
          utm_medium: "bookshelf-home-expired-card",
          utm_campaign: asset.manageAsset.vbid,
        }),
      });
    }
    if (isBook(asset.manageAsset)) {
      links.push({
        descriptor: "asset-details",
        label: intl.formatMessage({ defaultMessage: "Book Details" }),
        onClick: actions.openDetailsPanel,
      });
    }
    return links;
  }, [intl, asset, actions, makeRenewalUrl, isStoreAsset]);

  const expiringSoonLink = useMemo(() => {
    if (
      asset.license &&
      isStoreAsset(asset.license) &&
      isExpiringSoon(asset.license)
    ) {
      return {
        descriptor: "buy-on-store" as const,
        label: intl.formatMessage({ defaultMessage: "Extend Access" }),
        href: makeRenewalUrl(asset.manageAsset.vbid, {
          utm_medium: "bookshelf-home-extend-card",
          utm_campaign: asset.manageAsset.vbid,
        }),
      };
    }
    return undefined;
  }, [intl, asset, makeRenewalUrl, isStoreAsset]);

  const getActiveCoursewareLinks = useCallback(() => {
    const links: PopoverLink[] = [];
    if (
      canBeOpenedInReader(asset.manageAsset, asset.license) ||
      (asset.explore && asset.manageAsset.kind === "online resource bundle")
    ) {
      links.push({
        descriptor: "open-book",
        label: intl.formatMessage({ defaultMessage: "Open Book" }),
        ...readerURL,
      });
      links.push({
        descriptor: "asset-details",
        label: intl.formatMessage({ defaultMessage: "Book Details" }),
        onClick: actions.openDetailsPanel,
      });
    }

    if (asset.manageAsset.ancillaries.length > 0) {
      links.push({
        descriptor: "view-ancillaries",
        label: intl.formatMessage({ defaultMessage: "View Ancillaries" }),
        onClick: actions.openAncillariesPanel,
      });
    }

    if (canShowCoursewareDetails(asset.manageAsset, asset.license)) {
      links.push({
        descriptor: "access-code-instructions",
        label: asset.license?.accessCode?.revealed
          ? intl.formatMessage({ defaultMessage: "View Access Code" })
          : intl.formatMessage({ defaultMessage: "Access Code Instructions" }),
        onClick: actions.openCoursewareDialog,
      });
    }

    if (canLaunchCourseware(asset.manageAsset, asset.license)) {
      links.push({
        descriptor: "access-code-website",
        label: intl.formatMessage({ defaultMessage: "Go to Website" }),
        href: asset.license?.accessCode?.href,
      });
    }

    return links;
  }, [intl, asset, actions, readerURL]);

  /**
   * These are for explore courseware without the necessary license information
   * to show courseware details, either because it is not yet activated or is in a context
   * where we don't have license info (such as on /explore)
   */
  const getFallbackCoursewareLinksForExplore = useCallback(() => {
    const links: PopoverLink[] = [];

    // A general "Open Book" for courseware is tolerable. If it's just a plain access
    // code the book will open and the student will have access to the necessary
    // details to access the content. Not much more we can do without an activate
    // plus license fetch.
    if (exploreAssetAction === ExploreAssetAction.Open) {
      links.push({
        descriptor: "open-book",
        label: intl.formatMessage({ defaultMessage: "Open Book" }),
        ...readerURL,
      });
    } else if (exploreAssetAction === ExploreAssetAction.Borrow) {
      links.push({
        descriptor: "borrow",
        label: intl.formatMessage({ defaultMessage: "Borrow" }),
        onClick: actions.activateAsset,
      });
    }

    if (asset.manageAsset.kind === "online resource bundle") {
      links.push({
        descriptor: "asset-details",
        label: intl.formatMessage({ defaultMessage: "Book Details" }),
        onClick: actions.openDetailsPanel,
      });
    }

    if (asset.manageAsset.ancillaries.length > 0) {
      links.push({
        descriptor: "view-ancillaries",
        label: intl.formatMessage({ defaultMessage: "View Ancillaries" }),
        onClick: actions.openAncillariesPanel,
      });
    }

    if (isReturnable) {
      links.push({
        descriptor: "return",
        label: intl.formatMessage({ defaultMessage: "Return" }),
        onClick: actions.returnAsset,
      });
    }

    return links;
  }, [intl, asset, actions, readerURL, exploreAssetAction, isReturnable]);

  const getActiveSubscriptionLinks = useCallback(() => {
    return [
      {
        descriptor: "asset-details" as const,
        label: intl.formatMessage({ defaultMessage: "Subscription Details" }),
        onClick: actions.openDetailsPanel,
      },
    ];
  }, [intl, actions]);

  const getActiveBookLinks = useCallback(() => {
    const links: PopoverLink[] = [];

    if (
      (!!asset.license && !asset.explore) ||
      exploreAssetAction === ExploreAssetAction.Open
    ) {
      // without a license there is no point in offering to open it
      // but an explore book without a license can be activated when opened
      links.push({
        descriptor: "open-book",
        label: intl.formatMessage({ defaultMessage: "Open Book" }),
        ...readerURL,
      });
    }

    if (
      exploreAssetAction === ExploreAssetAction.Borrow &&
      actions.activateAsset
    ) {
      links.push({
        descriptor: "borrow",
        label: intl.formatMessage({ defaultMessage: "Borrow" }),
        onClick: actions.activateAsset,
      });
    }

    links.push({
      descriptor: "asset-details",
      label: intl.formatMessage({ defaultMessage: "Book Details" }),
      onClick: actions.openDetailsPanel,
    });

    if (asset.manageAsset.ancillaries.length > 0) {
      links.push({
        descriptor: "view-ancillaries",
        label: intl.formatMessage({ defaultMessage: "View Ancillaries" }),
        onClick: actions.openAncillariesPanel,
      });
    }

    if (isReturnable) {
      links.push({
        descriptor: "return",
        label: intl.formatMessage({ defaultMessage: "Return" }),
        onClick: actions.returnAsset,
      });
    }

    const canSearchAsset = !!asset.explore?.distributable;
    if (opts.showContentSearch && brand.Search.enabled && canSearchAsset) {
      links.push({
        descriptor: "search-content",
        label: intl.formatMessage({ defaultMessage: "Search Inside Book" }),
        onClick: actions.openContentSearchPanel,
      });
    }

    return links;
  }, [
    intl,
    actions,
    readerURL,
    asset,
    exploreAssetAction,
    opts.showContentSearch,
    brand,
    isReturnable,
  ]);

  return useMemo(() => {
    let links: PopoverLink[] = [];
    if (asset.license?.expired && !asset.explore) {
      links = getExpiredLinks();
    } else if (asset.manageAsset.kind === "subscription") {
      // This shouldn't actually appear, but just in case, show something reasonable
      links = getActiveSubscriptionLinks();
    } else if (
      isCourseware(asset.manageAsset) &&
      asset.explore &&
      !asset.license
    ) {
      links = getFallbackCoursewareLinksForExplore();
    } else if (isCourseware(asset.manageAsset)) {
      links = getActiveCoursewareLinks();
    } else {
      links = getActiveBookLinks();
    }
    // Maintain the primary action, but prioritize the expiring soon link
    if (expiringSoonLink) {
      if (links.length === 0) {
        links.push(expiringSoonLink);
      } else {
        links.splice(1, 0, expiringSoonLink);
      }
    }

    return [
      ...links,
      ...[a11yClaimsMenuLink, favoritesMenuLink as PopoverLink],
    ];
  }, [
    asset.explore,
    asset.license,
    asset.manageAsset,
    expiringSoonLink,
    favoritesMenuLink,
    a11yClaimsMenuLink,
    getExpiredLinks,
    getActiveSubscriptionLinks,
    getActiveCoursewareLinks,
    getActiveBookLinks,
    getFallbackCoursewareLinksForExplore,
  ]);
}
